import 'react-responsive-carousel/lib/styles/carousel.min.css';
import * as cookie from 'cookie';
import Head from 'next/head';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useRouter } from 'next/router';
import autosize from 'autosize/dist/autosize';
import useSWR from 'swr';
import { http } from '../../lib/api/http';
import { V3, V4 } from '../../lib/api/urls';
import { Container } from '../../components/layout/Container';
import {
  CITIES_API_LIMIT,
  DEFAULT_SEO_DESCRIPTION,
  DEFAULT_SEO_TITLE,
  DEVICE_SCREENS,
  DEVICE_SCREENS_MAX_STRING,
} from '../../lib/consts';
import { Header } from '../../components/layout/Header';
import { PostCard } from '../../components/posts/post-card';
import { LinkLikeButton } from '../../components/styled/Link';
import { ButtonShowMore } from '../../components/Buttons';
import { Pagination } from '../../components/Pagination';
import FilterSVG from '../../public/img/filter.svg';
import CheckSVG from '../../public/img/check-red.svg';
import { H3 } from '../../components/styled/typography';
import { getEventTextFromCount } from '../../utils';
import GorbiletRecommends from '../../components/blocks/GorbiletRecommends';
import { activeStatus } from '../../constants/events';
import { useModal } from '../../providers/modal';
import { GB_DOMAIN } from '../../lib/api/apiUrl';
import { useMainPreloader } from '../../components/loader/useMainPreloader';
import MainLoader from '../../components/loader/MainLoader';
import { useLoading } from '../../providers/loading';

function SearchBlock({
  showMobileSort,
  sortArray,
  setQuery,
  query,
  setShowMobileSort,
  setShowResults,
  searchResults,
  showResults,
  textArea,
  textareaHandler,
  searchValue,
  currentCity,
  setIsNewData,
  isNewData,
  setSearchResults,
  routerIsLoading,
}) {
  const theme = useTheme();
  const [isAdaptive, setIsAdaptive] = useState(false);
  const LIMIT_CARDS = 6;

  useEffect(() => {
    if (window.innerWidth <= DEVICE_SCREENS.md) {
      setIsAdaptive(true);
    }
  }, []);

  const getSearchResult = async () => {
    const data = { searchResults: null, similarResults: null };
    if (query.q !== '') {
      const results = await http.get(V4.events, {
        is_web: 'true',
        city: currentCity.id,
        limit: LIMIT_CARDS,
        offset: (query.page - 1) * LIMIT_CARDS,
        ordering: query.ordering,
        status: 'active',
        q: query.q,
      });
      if (isNewData) {
        setSearchResults({
          ...results,
          results: searchResults.results.concat(results.results),
        });
        data.searchResults = {
          ...results,
          results: searchResults.results.concat(results.results),
        };
      } else {
        setSearchResults(results);
        data.searchResults = results;
      }
      if (results.results.length > 0) {
        const similar = await http.get(
          `${V4.baseUrl}events/${results.results[0].id}/similar/`,
          {
            is_web: 'true',
            limit: 8,
            offset: 0,
          }
        );
        if (similar.count) {
          data.similarResults = similar;
        } else {
          data.similarResults = null;
        }
      } else {
        data.similarResults = null;
      }
    }
    return { ...data };
  };

  const { data } = useSWR(() => JSON.stringify(query), getSearchResult, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  if (showMobileSort) {
    return (
      <Container css={{ position: 'relative' }}>
        <H3
          css={{
            fontFamily: 'Montserrat',
            fontWeight: 800,
            fontSize: 22,
            color: theme.colors.text,
            padding: 16,
          }}
        >
          Сортировка по
        </H3>
        <div css={{ background: theme.colors.white }}>
          {sortArray.map((item) => (
            <button
              type='button'
              key={`mobileSort-${item.value}`}
              css={{
                fontFamily: 'Montserrat',
                color: theme.colors.black,
                fontWeight: 700,
                fontSize: 18,
                height: 75,
                width: '100%',
                paddingLeft: 16,
                paddingTop: 30,
                display: 'inline-flex',
                justifyContent: 'space-between',
                boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
              }}
              onClick={() => setQuery({ ...query, ordering: item.value })}
            >
              <div css={{ '::first-letter': { textTransform: 'uppercase' } }}>
                {item.title}
              </div>
              <div>
                {query.ordering === item.value && (
                  <CheckSVG
                    css={{
                      fill: 'red',
                      width: 24,
                      height: 24,
                      marginRight: 20,
                    }}
                  />
                )}
              </div>
            </button>
          ))}
        </div>
        <button
          // todo вынести кнопки отдельно
          css={{
            position: 'fixed',
            left: '50%',
            zIndex: 10,
            bottom: 30,
            transform: 'translateX(-50%)',
            display: 'none',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontSize: 16,
            height: 36,
            color: theme.colors.white,
            padding: '9px 10px',
            lineHeight: '18px',
            backgroundImage:
              'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
            backgroundSize: '500% 500%',
            margin: '16px 0',
            borderRadius: 20,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '75%',
            },
          }}
          type='button'
          onClick={() => {
            setShowMobileSort(false);
            setShowResults(true);
          }}
        >
          Показать {data?.searchResults?.results.length}{' '}
          {getEventTextFromCount(data?.searchResults?.results.length)}
        </button>
        <button
          css={{
            position: 'fixed',
            left: '50%',
            zIndex: 10,
            bottom: 10,
            transform: 'translateX(-50%)',
            display: 'none',
            fontFamily: 'Montserrat',
            fontWeight: 400,
            fontSize: 14,
            height: 36,
            color: theme.colors.red,
            padding: '9px 10px',
            lineHeight: '18px',
            // margin: '16px 0',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '75%',
            },
          }}
          type='button'
          onClick={() => {
            setQuery({ ...query, ordering: sortArray[0].value });
            setShowResults(true);
            setShowMobileSort(false);
          }}
        >
          Сбросить
        </button>
      </Container>
    );
  }
  return (
    <Container
      css={{
        position: 'relative',
        width: '100%',
        maxWidth: '1280px',
        padding: '45px 120px 0',
        opacity: routerIsLoading ? 0.2 : 1,
        [DEVICE_SCREENS_MAX_STRING.xxl]: {
          maxWidth: '1124px',
          padding: '45px 120px 0',
        },
        [DEVICE_SCREENS_MAX_STRING.xl]: {
          maxWidth: '960px',
          padding: '45px 53px',
        },
        [DEVICE_SCREENS_MAX_STRING.lg]: {
          maxWidth: '720px',
          padding: '45px 8px',
        },
        [DEVICE_SCREENS_MAX_STRING.md]: {
          maxWidth: '540px',
          padding: '45px 16px 0',
        },
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          maxWidth: '540px',
        },
      }}
    >
      {data?.searchResults?.count > 0 && (
        <button
          css={{
            position: 'fixed',
            left: '50%',
            zIndex: 10,
            bottom: 8,
            transform: 'translateX(-50%)',
            display: 'none',
            fontFamily: 'Golos',
            fontWeight: 500,
            fontSize: 16,
            height: 36,
            color: theme.colors.white,
            padding: '9px 10px',
            lineHeight: '18px',
            backgroundImage:
              'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
            backgroundSize: '500% 500%',
            margin: '16px 0',
            borderRadius: 20,
            // opacity: searchResults?.count > 0 ? 1 : '0.5',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '75%',
            },
          }}
          type='button'
          onClick={() =>
            data.searchResults.count > 0 && setShowMobileSort(true)
          }
        >
          Сортировка
          <FilterSVG
            css={{
              width: 20,
              height: 20,
              fill: theme.colors.white,
              marginLeft: 8,
            }}
          />
        </button>
      )}

      <div css={{ display: 'flex', justifyContent: 'center' }}>
        <div css={{ width: '100%' }}>
          {(!showResults || !isAdaptive) && (
            <>
              <div
                css={{
                  fontWeight: 400,
                  fontSize: 14,
                  fontFamily: 'Golos',
                  color: theme.colors.text,
                }}
              >
                Начните вводить слово
              </div>
              <form action=''>
                <textarea
                  ref={textArea}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                    }
                  }}
                  onChange={textareaHandler}
                  value={searchValue}
                  placeholder='Например: Александринский театр'
                  maxLength={400}
                  css={{
                    minHeight: 61,
                    height: 61,
                    resize: 'none',
                    fontSize: 32,
                    fontWeight: 800,
                    color: '#333333',
                    background: 'transparent',
                    border: 'none',
                    borderBottomColor: 'red',
                    borderBottomStyle: 'none',
                    borderBottomWidth: 'medium',
                    width: '100%',
                    borderBottom: '1px solid #333333',
                    lineHeight: '1.6em',
                    overflow: 'hidden',
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      fontSize: 22,
                      height: 40,
                      minHeight: 0,
                    },
                    '::placeholder': {
                      color: 'rgba(51, 51, 51, 0.2)',
                      fontWeight: 700,
                      fontFamily: 'Montserrat',
                    },
                  }}
                />
              </form>
            </>
          )}
          <button
            type='button'
            css={{
              display: 'none',
              fontFamily: 'Golos',
              fontWeight: 500,
              fontSize: 16,
              height: 40,
              color: theme.colors.white,
              padding: '9px 10px',
              lineHeight: '18px',
              backgroundImage:
                'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
              backgroundSize: '500% 500%',
              margin: showResults ? '-28px 0 0' : '16px 0',
              borderRadius: 4,
              opacity: searchResults?.count > 0 ? 1 : '0.5',
              transition: 'all 0.2s ease',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              },
            }}
            onClick={() =>
              data?.searchResults?.count > 0 && setShowResults(!showResults)
            }
          >
            {showResults ? 'Новый поиск' : 'Показать события'}
          </button>
        </div>
      </div>
      {data?.searchResults && data?.searchResults.count === 0 && (
        <div
          css={{
            marginTop: 60,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'none',
            },
          }}
        >
          <div
            css={{
              width: '100%',
            }}
          >
            <div
              css={{
                fontFamily: 'Montserrat',
                fontWeight: 700,
                fontSize: 26,
                textAlign: 'center',
                color: theme.colors.text,
              }}
            >
              К сожалению, ничего не нашлось
            </div>
            <div
              css={{
                fontFamily: 'Golos',
                fontWeight: 400,
                fontSize: 14,
                textAlign: 'center',
                color: theme.colors.text,
              }}
            >
              Попробуйте изменить запрос.
            </div>
          </div>
        </div>
      )}
      {data?.searchResults &&
        data?.searchResults.count !== 0 &&
        (showResults || !isAdaptive) && (
          <div
            css={{
              marginTop: 20,
              marginBottom: 70,
              [DEVICE_SCREENS_MAX_STRING.md]: {
                paddingBottom: 90,
                marginBottom: 0,
              },
            }}
          >
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                },
              }}
            >
              <div
                css={{
                  fontFamily: 'Montserrat',
                  fontWeight: 700,
                  fontSize: 26,
                  lineHeight: '31px',
                  color: theme.colors.text,
                }}
              >
                Результаты поиска{' '}
                <span css={{ color: 'gray' }}>
                  ({data?.searchResults.count})
                </span>
              </div>
              <div
                css={{
                  display: 'flex',
                  gap: 10,
                  fontFamily: 'Golos',
                  fontWeight: 400,
                  lineHeight: '22px',
                  fontSize: 12,
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'none',
                  },
                }}
              >
                <div css={{ color: theme.colors.text }}>Сортировать по:</div>
                {sortArray.map((sortItem) => (
                  <button
                    key={`SortButton__${sortItem.title}`}
                    onClick={() => {
                      setQuery({
                        ...query,
                        ordering: sortItem.value,
                        page: 1,
                      });
                    }}
                    type='button'
                    css={{
                      color:
                        sortItem.value === query.ordering
                          ? theme.colors.red
                          : theme.colors.text,
                    }}
                  >
                    {sortItem.title}
                  </button>
                ))}
              </div>
            </div>

            <div
              css={{
                ...theme.mixins.flex(),
                margin: '20px -8px 0 -8px',
              }}
            >
              {data?.searchResults?.results.map((item) => (
                <PostCard
                  catalog
                  key={item.id}
                  item={item}
                  currentCity={currentCity}
                />
              ))}
            </div>
            <div css={{ padding: '0 8px' }}>
              {data?.searchResults?.next && (
                <ButtonShowMore
                  callback={() => {
                    setIsNewData(true);
                    setQuery({ ...query, page: Number(query.page) + 1 });
                  }}
                />
              )}
              {data?.searchResults?.count > LIMIT_CARDS && (
                <Pagination
                  currentPage={query.page - 1}
                  onPrevPage={() =>
                    setQuery({
                      ...query,
                      page:
                        Number(query.page) - 1 !== 0
                          ? Number(query.page) - 1
                          : 1,
                    })
                  }
                  count={Math.ceil(data.searchResults.count / LIMIT_CARDS)}
                  onNextPage={() =>
                    setQuery({
                      ...query,
                      page:
                        Number(query.page + 1) <
                        Math.ceil(data.searchResults.count / LIMIT_CARDS)
                          ? Number(query.page + 1)
                          : Math.ceil(data.searchResults.count / LIMIT_CARDS),
                    })
                  }
                  changePage={(event) =>
                    setQuery({
                      ...query,
                      page:
                        (event.selected ?? event.currentTarget.dataset.page) +
                        1,
                    })
                  }
                />
              )}
            </div>
          </div>
        )}

      {data?.searchResults && !data?.similarResults && <GorbiletRecommends />}
      {data?.searchResults &&
        data?.similarResults &&
        data?.similarResults.count !== 0 &&
        (showResults || !isAdaptive) && (
          <div
            css={{
              marginBottom: '4rem',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                marginBottom: '2rem',
              },
            }}
          >
            <div
              css={{
                fontFamily: 'Montserrat',
                fontWeight: 700,
                fontSize: 26,
                color: theme.colors.textSecondary,
                paddingBottom: 8,
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  marginLeft: 8,
                },
              }}
            >
              Похожие мероприятия
            </div>
            <div
              // scroll-wrapper
              css={{
                width: '100%',
                margin: '0 -8px 0 -8px',
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  width: '100%',
                  '&>*': {
                    whiteSpace: 'normal',
                  },
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                },
              }}
            >
              <div
                css={{
                  ...theme.mixins.flex(),
                  overflow: 'auto',
                  marginTop: -15,
                  paddingTop: 15,
                  width: '100%',
                  '::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    flexWrap: 'nowrap',
                  },
                }}
              >
                {data?.similarResults.results.map((item) => (
                  <PostCard
                    scroll
                    key={item.id}
                    item={item}
                    currentCity={currentCity}
                  />
                ))}
              </div>
            </div>
            <div
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <LinkLikeButton href={[currentCity.url, 'catalog', ''].join('/')}>
                Все мероприятия
              </LinkLikeButton>
            </div>
          </div>
        )}
    </Container>
  );
}

export default function Page({ currentCity, cities, categoriesForHeader }) {
  const router = useRouter();
  const [searchResults, setSearchResults] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isNewData, setIsNewData] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const { routerIsLoading } = useMainPreloader();
  const { LoaderComponent, isLoading } = useLoading();
  const textArea = useRef(null);
  const { Modal } = useModal();
  const [query, setQuery] = useState({
    ordering: '-views_per_3days',
    page: 1,
    q: '',
  });

  const sortArray = [
    {
      value: '-views_per_3days',
      title: 'популярности',
    },
    {
      value: '-activated_at',
      title: 'новинкам',
    },
    {
      value: 'filter_price',
      title: 'возрастанию цены',
    },
    {
      value: '-filter_price',
      title: 'убыванию цены',
    },
    {
      value: '-discount',
      title: 'размеру скидки',
    },
  ];

  const onSearch = (value) => {
    const valueWithoutSpace = value.trim();
    if (valueWithoutSpace !== '') {
      setQuery({ ...query, q: valueWithoutSpace, page: 1 });
      setIsNewData(false);
    } else {
      setSearchResults(false);
    }
  };

  useEffect(() => {
    if (query.q !== '') {
      router.replace({
        query: { ...router.query, ...query },
      });
    } else {
      setSearchResults(false);
    }
    setIsNewData(false);
  }, [query]);

  useEffect(() => {
    setQuery({
      q: router.query.q ?? '',
      page: router.query.page ?? 1,
      ordering: router.query.ordering ?? '-views_per_3days',
    });
    setSearchValue(router.query.q ?? '');
  }, []);

  useEffect(() => {
    if (textArea.current) {
      autosize(textArea.current);
    }
  }, [textArea, showResults]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(_.debounce(onSearch, 1000), []);

  const textareaHandler = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    debounceSearch(value);
  };
  return (
    <div>
      <Head>
        <title>{DEFAULT_SEO_TITLE}</title>
        <meta name='description' content={DEFAULT_SEO_DESCRIPTION} />
        <meta name='viewport' content='initial-scale=1.0, width=device-width' />
        <meta charSet='utf-8' />
        <meta
          httpEquiv='X-UA-Compatible'
          content='IE=edge'
          data-meta-dynamic='true'
        />
        <link rel='canonical' href={`${GB_DOMAIN}/search/`} />
      </Head>
      <Header
        currentCity={currentCity}
        cities={cities}
        categoriesForHeader={categoriesForHeader}
      />
      <SearchBlock
        routerIsLoading={routerIsLoading || isLoading}
        showMobileSort={showMobileSort}
        sortArray={sortArray}
        setQuery={setQuery}
        query={query}
        setShowMobileSort={setShowMobileSort}
        setShowResults={setShowResults}
        searchResults={searchResults}
        showResults={showResults}
        textArea={textArea}
        textareaHandler={textareaHandler}
        searchValue={searchValue}
        currentCity={currentCity}
        setIsNewData={setIsNewData}
        isNewData={isNewData}
        setSearchResults={setSearchResults}
      />
      {routerIsLoading && <MainLoader />}
      <LoaderComponent />
      <Modal />
    </div>
  );
}

export async function getServerSideProps(context) {
  const cities = await http.get(V3.cities, { limit: CITIES_API_LIMIT });
  let currentCityUrl;
  let parsedCookies = {};
  if (context.req.headers.cookie) {
    parsedCookies = cookie.parse(context.req.headers.cookie);
  }
  if (parsedCookies['current-city']) {
    currentCityUrl = parsedCookies['current-city'];
  } else {
    currentCityUrl = cities.results[0].url;
  }

  const currentCity = cities.results.find(
    (city) => city.url === currentCityUrl
  );

  const categories = await http.get(V3.categories, {
    city: currentCity.id,
    is_web: true,
    status: activeStatus,
    sort: 'sortOrder',
    limit: 1000,
    level: 0,
  });

  const adpCategories = await http.get(V3.adpCategories(currentCity.id));

  const categoriesForHeader = [];

  for (let i = 0; i < adpCategories.categories.length; i += 1) {
    const el = categories.results.find(
      (item) => item.id === adpCategories.categories[i]
    );
    if (el) {
      categoriesForHeader.push(el);
    }
  }

  return {
    props: {
      currentCity,
      cities,
      categoriesForHeader,
    },
  };
}
